// ClientPage.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../footer/Footer";
import dayjs from "dayjs";

// MUI Icons
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AppsIcon from "@mui/icons-material/Apps";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Componenti Pagine
import ManageClient from "./ManageClient";
import ManagementCost from "../Costs/ManagementCost";
import Statistics from "../statistics/statistics";
import Orders from "../orders/Orders";
import Gestione from "./Gestione/Gestione";
import Menu from "../menu/menu";
import CreaMenu from "../menu/creaMenu";

import { hasOrdersForMonth, hasCostsForMonth } from "../utility/EndPointGoogleFirebase";
import { signOut } from "firebase/auth";
import "./client.css";

const TABS = [
  { value: "1", label: "Cliente",     Icon: PersonOutlineIcon,        bottomNav: false },
  { value: "2", label: "Ordini",      Icon: ReceiptLongIcon,          bottomNav: true  },
  { value: "3", label: "Spese",       Icon: PaymentsIcon,             bottomNav: true  },
  { value: "4", label: "Statistiche", Icon: InsertChartOutlinedIcon,  bottomNav: true  },
  { value: "5", label: "Gestione",    Icon: SettingsApplicationsIcon, bottomNav: false },
  { value: "6", label: "Crea Menu",   Icon: LibraryAddIcon,           bottomNav: false },
  { value: "7", label: "Menu",        Icon: RestaurantMenuIcon,       bottomNav: false },
];

const ACCENT = "#4f46e5";
const ACCENT_LIGHT = "#eef2ff";

function ClientPage({ auth }) {
  const [selectedTab, setSelectedTab] = useState("2");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mesiDaPulire, setMesiDaPulire] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [tabKeys, setTabKeys] = useState({ "1": 0, "2": 0, "3": 0, "4": 0, "5": 0, "6": 0, "7": 0 });

  const navigate = useNavigate();
  const location = useLocation();
  const username = location.state?.username;

  // ── Controlla mesi precedenti con dati reali da Firebase ──────────
  useEffect(() => {
    const checkMesiDaPulire = async () => {
      const meseCorrente = dayjs().month() + 1;
      const annoCorrente = dayjs().year();
      let count = 0;

      for (let m = 1; m < meseCorrente; m++) {
        const [hasOrdini, hasSpese] = await Promise.all([
          hasOrdersForMonth(m, annoCorrente),
          hasCostsForMonth(m, annoCorrente),
        ]);
        if (hasOrdini || hasSpese) count++;
      }

      setMesiDaPulire(Math.min(count, 9));

      // ✅ Mostra il toast solo se ci sono dati da pulire
      if (count > 0) {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000); // scompare dopo 5 secondi
      }
    };

    checkMesiDaPulire();
  }, []);

  const handleLogout = () => {
    if (!auth) { navigate("/login"); return; }
    signOut(auth)
      .then(() => navigate("/login"))
      .catch((e) => console.error(e));
  };

  const selectTab = (val) => {
    setTabKeys(prev => ({ ...prev, [val]: prev[val] + 1 }));
    setSelectedTab(val);
    setDrawerOpen(false);
  };

  const handleToastClick = () => {
    setShowToast(false);
    selectTab("5"); // Va direttamente a Gestione
  };

  const currentTab = TABS.find(t => t.value === selectedTab);
  const bottomTabs = TABS.filter(t => t.bottomNav);
  const drawerTabs = TABS.filter(t => !t.bottomNav);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f9fafb" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500&display=swap');
        * { box-sizing: border-box; }

        /* ── Desktop sidebar ── */
        .cp-sidebar { display: none; }
        @media (min-width: 768px) {
          .cp-sidebar { display: flex; }
          .cp-mobile-header { display: none !important; }
          .cp-bottom-nav { display: none !important; }
          .cp-main { margin-left: 220px; }
        }

        .cp-sidebar {
          position: fixed; top: 0; left: 0; bottom: 0;
          width: 220px; z-index: 100;
          flex-direction: column;
          background: #fff;
          border-right: 1px solid #e5e7eb;
          box-shadow: 2px 0 12px rgba(0,0,0,0.04);
        }
        .cp-sidebar-logo { padding: 20px 20px 16px; border-bottom: 1px solid #f3f4f6; }
        .cp-sidebar-logo-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 15px; font-weight: 500; color: #111827; line-height: 1.3;
        }
        .cp-sidebar-logo-sub {
          font-size: 10px; color: #9ca3af;
          letter-spacing: 0.1em; text-transform: uppercase; margin-top: 2px;
        }
        .cp-sidebar-nav {
          flex: 1; overflow-y: auto; padding: 10px;
          display: flex; flex-direction: column; gap: 2px;
        }
        .cp-nav-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 12px; border-radius: 10px;
          cursor: pointer; user-select: none;
          font-size: 13px; font-weight: 500; color: #6b7280;
          transition: background 0.15s, color 0.15s;
          border: none; background: none; width: 100%; text-align: left;
          -webkit-tap-highlight-color: transparent;
        }
        .cp-nav-item:hover { background: #f3f4f6; color: #111827; }
        .cp-nav-item.active { background: ${ACCENT_LIGHT}; color: ${ACCENT}; font-weight: 600; }
        .cp-nav-item svg { font-size: 18px !important; flex-shrink: 0; }
        .cp-sidebar-footer { padding: 14px 10px; border-top: 1px solid #f3f4f6; }
        .cp-user-row { display: flex; align-items: center; gap: 10px; padding: 8px 12px; margin-bottom: 4px; }
        .cp-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; border: 2px solid #e5e7eb; flex-shrink: 0; }
        .cp-username { font-size: 12px; font-weight: 500; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cp-logout-btn {
          display: flex; align-items: center; gap: 8px;
          padding: 9px 12px; border-radius: 10px;
          font-size: 13px; font-weight: 500; color: #dc2626;
          cursor: pointer; border: none; background: none;
          width: 100%; transition: background 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        .cp-logout-btn:hover { background: #fff1f2; }

        /* ── Mobile header ── */
        .cp-mobile-header {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          height: 54px; background: #fff;
          border-bottom: 1px solid #e5e7eb;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 16px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .cp-mobile-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 16px; font-weight: 500; color: #111827;
        }
        .cp-mobile-menu-btn {
          width: 36px; height: 36px; border-radius: 10px;
          border: 1px solid #e5e7eb; background: #f9fafb;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: #374151;
          -webkit-tap-highlight-color: transparent;
        }

        /* ── Bottom nav ── */
        .cp-bottom-nav {
          position: fixed; bottom: 0; left: 0; right: 0; z-index: 100;
          background: #fff; border-top: 1px solid #e5e7eb;
          box-shadow: 0 -2px 12px rgba(0,0,0,0.06);
          display: flex; height: 62px;
          padding-bottom: env(safe-area-inset-bottom);
        }
        .cp-bottom-item {
          flex: 1; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 3px;
          cursor: pointer; border: none; background: none;
          color: #9ca3af; font-size: 10px; font-weight: 500;
          letter-spacing: 0.04em; text-transform: uppercase;
          transition: color 0.15s; -webkit-tap-highlight-color: transparent;
        }
        .cp-bottom-item svg { font-size: 22px !important; }
        .cp-bottom-item.active { color: ${ACCENT}; }
        .cp-bottom-item-other { color: #374151; }
        .cp-bottom-item-other.drawer-open { color: ${ACCENT}; }

        /* ── Drawer mobile ── */
        .cp-drawer-overlay {
          position: fixed; inset: 0; z-index: 200;
          background: rgba(17,24,39,0.4);
          backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);
          animation: overlayIn 0.2s ease;
        }
        @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
        .cp-drawer {
          position: fixed; top: 0; right: 0; bottom: 0; z-index: 201;
          width: min(300px, 85vw); background: #fff;
          border-left: 1px solid #e5e7eb;
          display: flex; flex-direction: column;
          animation: drawerIn 0.22s cubic-bezier(0.22,1,0.36,1);
          box-shadow: -8px 0 40px rgba(0,0,0,0.12);
        }
        @keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .cp-drawer-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 18px; border-bottom: 1px solid #f3f4f6;
        }
        .cp-drawer-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 16px; font-weight: 500; color: #111827;
        }
        .cp-drawer-close {
          width: 32px; height: 32px; border-radius: 8px;
          border: 1px solid #e5e7eb; background: #f9fafb;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: #374151;
        }
        .cp-drawer-list {
          flex: 1; overflow-y: auto; padding: 10px;
          display: flex; flex-direction: column; gap: 2px;
        }
        .cp-drawer-item {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 14px; border-radius: 10px;
          cursor: pointer; border: none; background: none; width: 100%;
          -webkit-tap-highlight-color: transparent; transition: background 0.15s;
        }
        .cp-drawer-item:hover { background: #f3f4f6; }
        .cp-drawer-item.active { background: ${ACCENT_LIGHT}; }
        .cp-drawer-item-left {
          display: flex; align-items: center; gap: 12px;
          font-size: 14px; font-weight: 500; color: #111827;
        }
        .cp-drawer-item.active .cp-drawer-item-left { color: ${ACCENT}; }
        .cp-drawer-item svg { font-size: 20px !important; color: #9ca3af; }
        .cp-drawer-item.active svg { color: ${ACCENT}; }
        .cp-drawer-footer { padding: 12px; border-top: 1px solid #f3f4f6; }

        /* ── Main content ── */
        .cp-main { flex: 1; padding-top: 54px; padding-bottom: 62px; }
        @media (min-width: 768px) {
          .cp-main { margin-left: 220px; padding-top: 0; padding-bottom: 0; }
        }
        .cp-page { display: none; }
        .cp-page.active { display: block; }

        /* ── Toast ── */
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes toastOut {
          from { opacity: 1; transform: translateX(-50%) translateY(0); }
          to   { opacity: 0; transform: translateX(-50%) translateY(-16px); }
        }
      `}</style>

      {/* ── TOAST notifica dati da pulire ── */}
      {showToast && (
        <div
          onClick={handleToastClick}
          style={{
            position: "fixed", top: 16, left: "50%",
            transform: "translateX(-50%)",
            zIndex: 300,
            background: "#fff",
            border: "1px solid #fed7aa",
            borderLeft: "4px solid #d97706",
            borderRadius: 12,
            padding: "12px 16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            display: "flex", alignItems: "center", gap: 10,
            cursor: "pointer", maxWidth: "calc(100vw - 32px)",
            animation: "toastIn 0.3s ease",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <span style={{ fontSize: 18 }}>🗂️</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#92400e", margin: "0 0 2px" }}>
              Hai dati vecchi da pulire
            </p>
            <p style={{ fontSize: 11, color: "#b45309", margin: 0 }}>
              {mesiDaPulire} {mesiDaPulire === 1 ? "mese" : "mesi"} con ordini o spese — tocca per andare in Gestione
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setShowToast(false); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#9ca3af", fontSize: 16, padding: 4,
              display: "flex", alignItems: "center",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── DESKTOP SIDEBAR ── */}
      <aside className="cp-sidebar">
        <div className="cp-sidebar-logo">
          <div className="cp-sidebar-logo-sub">Gestionale</div>
          <div className="cp-sidebar-logo-title">Nuovo Forno<br />dal 1966</div>
        </div>
        <nav className="cp-sidebar-nav">
          {TABS.map(({ value, label, Icon }) => (
            <button
              key={value}
              className={`cp-nav-item${selectedTab === value ? " active" : ""}`}
              onClick={() => selectTab(value)}
            >
              <Icon />
              {label}
              {value === "5" && mesiDaPulire > 0 && (
                <span style={{
                  marginLeft: "auto",
                  background: "#dc2626", color: "#fff",
                  borderRadius: 99, fontSize: 10, fontWeight: 600,
                  minWidth: 18, height: 18,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "0 5px",
                }}>
                  {mesiDaPulire}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="cp-sidebar-footer">
          <div className="cp-user-row">
            <img src="/img/matteo.jpg" alt={username} className="cp-avatar" />
            <span className="cp-username">{username || "Utente"}</span>
          </div>
          <button className="cp-logout-btn" onClick={handleLogout}>
            <ExitToAppIcon style={{ fontSize: 18 }} /> Logout
          </button>
        </div>
      </aside>

      {/* ── MOBILE HEADER ── */}
      <header className="cp-mobile-header">
        <span className="cp-mobile-title">{currentTab?.label || "Gestionale"}</span>
        <button className="cp-mobile-menu-btn" onClick={() => setDrawerOpen(true)}>
          <AppsIcon style={{ fontSize: 20 }} />
        </button>
      </header>

      {/* ── MOBILE DRAWER ── */}
      {drawerOpen && (
        <>
          <div className="cp-drawer-overlay" onClick={() => setDrawerOpen(false)} />
          <div className="cp-drawer">
            <div className="cp-drawer-header">
              <span className="cp-drawer-title">Menu</span>
              <button className="cp-drawer-close" onClick={() => setDrawerOpen(false)}>
                <CloseIcon style={{ fontSize: 18 }} />
              </button>
            </div>
            <div className="cp-drawer-list">
              {drawerTabs.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  className={`cp-drawer-item${selectedTab === value ? " active" : ""}`}
                  onClick={() => selectTab(value)}
                >
                  <span className="cp-drawer-item-left">
                    <Icon />
                    {label}
                    {value === "5" && mesiDaPulire > 0 && (
                      <span style={{
                        background: "#dc2626", color: "#fff",
                        borderRadius: 99, fontSize: 10, fontWeight: 600,
                        padding: "2px 7px", marginLeft: 6,
                      }}>
                        {mesiDaPulire} {mesiDaPulire === 1 ? "mese" : "mesi"}
                      </span>
                    )}
                  </span>
                  <ArrowForwardIosIcon style={{ fontSize: 13, color: "#d1d5db" }} />
                </button>
              ))}
            </div>
            <div className="cp-drawer-footer">
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px 12px" }}>
                <img src="/img/matteo.jpg" alt={username} className="cp-avatar" />
                <span className="cp-username">{username || "Utente"}</span>
              </div>
              <button className="cp-logout-btn" onClick={handleLogout}>
                <ExitToAppIcon style={{ fontSize: 18 }} /> Logout
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── MAIN CONTENT ── */}
      <main className="cp-main">
        <div className={`cp-page${selectedTab === "1" ? " active" : ""}`}><ManageClient key={tabKeys["1"]} /></div>
        <div className={`cp-page${selectedTab === "2" ? " active" : ""}`}><div id="container" className="orders-container"><Orders key={tabKeys["2"]} /></div></div>
        <div className={`cp-page${selectedTab === "3" ? " active" : ""}`}><ManagementCost key={tabKeys["3"]} /></div>
        <div className={`cp-page${selectedTab === "4" ? " active" : ""}`}><Statistics key={tabKeys["4"]} /></div>
        <div className={`cp-page${selectedTab === "5" ? " active" : ""}`}><Gestione key={tabKeys["5"]} mesiDaPulire={mesiDaPulire} /></div>
        <div className={`cp-page${selectedTab === "6" ? " active" : ""}`}><CreaMenu key={tabKeys["6"]} /></div>
        <div className={`cp-page${selectedTab === "7" ? " active" : ""}`}><Menu key={tabKeys["7"]} isProtected={true} /></div>
      </main>

      {/* ── BOTTOM NAV MOBILE ── */}
      <nav className="cp-bottom-nav">
        {bottomTabs.map(({ value, label, Icon }) => (
          <button
            key={value}
            className={`cp-bottom-item${selectedTab === value ? " active" : ""}`}
            onClick={() => selectTab(value)}
          >
            <Icon />
            {label}
          </button>
        ))}
        <button
          className={`cp-bottom-item cp-bottom-item-other${drawerOpen ? " drawer-open" : ""}`}
          onClick={() => setDrawerOpen(true)}
        >
          <div style={{ position: "relative", display: "inline-flex" }}>
            <AppsIcon />
            {mesiDaPulire > 0 && (
              <span style={{
                position: "absolute", top: -4, right: -6,
                background: "#dc2626", color: "#fff",
                borderRadius: 99, fontSize: 9, fontWeight: 600,
                minWidth: 14, height: 14,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 3px", lineHeight: 1,
              }}>
                {mesiDaPulire}
              </span>
            )}
          </div>
          Altro
        </button>
      </nav>

      <Footer />
    </div>
  );
}

export default ClientPage;